/**
 * THE URL CODEC — the filter object, as a query string.
 *
 * Three properties, and the tests pin all three:
 *
 *   1. `decode(encode(f)) === f` for every field. If it did not hold, a saved
 *      view and a pasted link would both restore something slightly other than
 *      what was saved, and nobody would notice until the numbers were wrong.
 *   2. ONLY NON-DEFAULT VALUES ARE WRITTEN. A default view is `/dashboard` with
 *      an empty query string. Writing defaults would make every URL a wall of
 *      parameters and make "did the reader choose this?" unanswerable.
 *   3. A VALUE THAT CANNOT BE READ BACK IS NEVER WRITTEN. Everything the codec
 *      emits round-trips through `URLSearchParams`, including the members of an
 *      array that contain a comma, a backslash or a space.
 *
 * ── THE KEYS ARE SHORT AND THEY ARE PERMANENT ──
 *
 * Two letters each. They are in links people have pasted into messages and into
 * saved views, so renaming one silently breaks every link that carries it.
 * Adding a key is free; changing or reusing one is not.
 *
 * ── THE LEGACY NAMES STILL DECODE ──
 *
 * /dashboard/analytics wrote `range`, `compare`, `measure`, `building`,
 * `portfolio`, `subject` and `drill`. Those bookmarks exist. They decode for one
 * release (LEGACY_KEYS below) and are never written back, so a legacy link
 * upgrades itself into the new form the first time the reader touches a control.
 */
import {
  Book,
  DashboardFilter,
  CompareScope,
  DEFAULT_FILTER,
  Density,
  Direction,
  DrillSelection,
  Granularity,
  SortState,
  Status,
  Tab,
} from './filter-model';
import { normalise } from './filter-reducer';
import {
  dayKeyIn,
  deviceZone,
  isDayKey,
} from '../../../../../function-utils/building-time';
import type { FilterMeta } from './filter-model';

/* ──────────────────────────────  the keys  ────────────────────────────── */

const K = {
  portfolioId: 'pf',
  buildingId: 'bl',
  unit: 'un',
  range: 'rg',
  from: 'fr',
  to: 'to',
  granularity: 'gr',
  compare: 'cp',
  timeFrom: 'tf',
  timeTo: 'tt',
  measure: 'ms',
  book: 'bk',
  subject: 'sj',
  turnoverBucket: 'tb',
  kinds: 'kn',
  severity: 'sv',
  groups: 'gp',
  actors: 'ac',
  role: 'rl',
  direction: 'dr',
  status: 'st',
  priority: 'pr',
  buildingActivity: 'ba',
  visitorType: 'vt',
  visitorDetails: 'vd',
  preAuthorized: 'pa',
  onPass: 'op',
  approval: 'ap',
  parkingValidation: 'pv',
  numberOfGuests: 'ng',
  carrier: 'cr',
  parcelStatus: 'ps',
  name: 'nm',
  lastName: 'ln',
  q: 'q',
  tab: 'tab',
  qualityDrill: 'qd',
  seriesOff: 'sr',
  density: 'dn',
  columns: 'co',
  sort: 'sc',
  page: 'pg',
  /* The drill selection is THREE parameters rather than one delimited value.
     Its `key` is a group key from the API — a building name, a staff id, an
     entry kind like "Emergency (911)" — and any delimiter this file chose could
     appear inside one. Three parameters need no delimiter and therefore need no
     escaping rule to get wrong. */
  drillSource: 'dls',
  drillDimension: 'dld',
  drillKey: 'dlk',
  drillLabel: 'dll',
  compareScopePortfolio: 'csp',
  compareScopeBuilding: 'csb',
} as const;

/** The names /dashboard/analytics used. Read, never written. */
const LEGACY_KEYS: Record<string, string> = {
  range: K.range,
  compare: K.compare,
  measure: K.measure,
  building: K.buildingId,
  portfolio: K.portfolioId,
  subject: K.subject,
  // On the old screen `drill` was the QUALITY drill and nothing else.
  drill: K.qualityDrill,
};

/* ───────────────────────────  array encoding  ─────────────────────────── */

/**
 * Comma-joined, with commas and backslashes escaped inside a member.
 *
 * No member of any vocabulary on this screen contains a comma today, and a test
 * asserts that against the live enums. The escaping is here anyway: the entry
 * kinds are free display labels the server can extend, and the day one of them
 * contains a comma is not the day to discover that the filter silently split it
 * into two kinds that match nothing.
 */
const joinList = (values: string[]): string =>
  values.map(value => value.replace(/\\/g, '\\\\').replace(/,/g, '\\,')).join(',');

const splitList = (raw: string): string[] => {
  const out: string[] = [];
  let current = '';
  let escaped = false;
  for (const char of raw) {
    if (escaped) {
      current += char;
      escaped = false;
      continue;
    }
    if (char === '\\') {
      escaped = true;
      continue;
    }
    if (char === ',') {
      out.push(current);
      current = '';
      continue;
    }
    current += char;
  }
  out.push(current);

  return out.filter(value => value !== '');
};

/* ────────────────────────────────  encode  ────────────────────────────── */

const sameList = (a: string[], b: string[]): boolean =>
  a.length === b.length && a.every((value, i) => value === b[i]);

/**
 * The filter, as parameters. Non-default values only.
 *
 * `base` lets a caller start from the query string that is ALREADY in the
 * address bar rather than from nothing, so a parameter this screen does not own
 * (an unrelated deep link) is preserved rather than deleted.
 */
export const encode = (
  filter: DashboardFilter,
  base?: URLSearchParams
): URLSearchParams => {
  const params = new URLSearchParams(base ? base.toString() : '');

  const put = (key: string, value: string | null | undefined) => {
    if (value == null || value === '') params.delete(key);
    else params.set(key, value);
  };
  const putIf = (
    key: string,
    value: string | number | null,
    isDefault: boolean
  ) => {
    if (isDefault || value == null || value === '') params.delete(key);
    else params.set(key, String(value));
  };
  const putList = (key: string, value: string[], fallback: string[]) => {
    if (sameList(value, fallback) || value.length === 0) params.delete(key);
    else params.set(key, joinList(value));
  };

  const d = DEFAULT_FILTER;

  putIf(K.portfolioId, filter.portfolioId, filter.portfolioId === d.portfolioId);
  putIf(K.buildingId, filter.buildingId, filter.buildingId === d.buildingId);
  putIf(K.unit, filter.unit, filter.unit === d.unit);

  putIf(K.range, filter.range, filter.range === d.range);
  putIf(K.from, filter.from, filter.from === d.from);
  putIf(K.to, filter.to, filter.to === d.to);
  putIf(K.granularity, filter.granularity, filter.granularity === d.granularity);
  putIf(K.compare, filter.compare, filter.compare === d.compare);
  putIf(K.timeFrom, filter.timeFrom, filter.timeFrom === d.timeFrom);
  putIf(K.timeTo, filter.timeTo, filter.timeTo === d.timeTo);

  putIf(K.measure, filter.measure, filter.measure === d.measure);
  putIf(K.book, filter.book, filter.book === d.book);
  putIf(K.subject, filter.subject, filter.subject === d.subject);
  putIf(
    K.turnoverBucket,
    filter.turnoverBucket,
    filter.turnoverBucket === d.turnoverBucket
  );

  putList(K.kinds, filter.kinds, d.kinds);
  putIf(K.severity, filter.severity, filter.severity === d.severity);
  putList(K.groups, filter.groups, d.groups);
  putList(K.actors, filter.actors, d.actors);
  putIf(K.role, filter.role, filter.role === d.role);
  putIf(K.direction, filter.direction, filter.direction === d.direction);
  putIf(K.status, filter.status, filter.status === d.status);
  putIf(K.priority, filter.priority, filter.priority === d.priority);
  putIf(
    K.buildingActivity,
    filter.buildingActivity,
    filter.buildingActivity === d.buildingActivity
  );
  putIf(K.visitorType, filter.visitorType, filter.visitorType === d.visitorType);
  putIf(
    K.visitorDetails,
    filter.visitorDetails,
    filter.visitorDetails === d.visitorDetails
  );
  putIf(K.preAuthorized, filter.preAuthorized, filter.preAuthorized === d.preAuthorized);
  putIf(K.onPass, filter.onPass, filter.onPass === d.onPass);
  putIf(K.approval, filter.approval, filter.approval === d.approval);
  putIf(
    K.parkingValidation,
    filter.parkingValidation,
    filter.parkingValidation === d.parkingValidation
  );
  putIf(
    K.numberOfGuests,
    filter.numberOfGuests,
    filter.numberOfGuests === d.numberOfGuests
  );
  putIf(K.carrier, filter.carrier, filter.carrier === d.carrier);
  putIf(K.parcelStatus, filter.parcelStatus, filter.parcelStatus === d.parcelStatus);
  putIf(K.name, filter.name, filter.name === d.name);
  putIf(K.lastName, filter.lastName, filter.lastName === d.lastName);

  putIf(K.q, filter.q, filter.q === d.q);

  putIf(K.tab, filter.tab, filter.tab === d.tab);
  putIf(K.qualityDrill, filter.qualityDrill, filter.qualityDrill === d.qualityDrill);
  putList(K.seriesOff, filter.seriesOff, d.seriesOff);
  putIf(K.density, filter.density, filter.density === d.density);
  putList(K.columns, filter.columns || [], []);
  putIf(K.page, filter.page, filter.page === d.page);

  if (filter.sort) params.set(K.sort, `${filter.sort.column}:${filter.sort.dir}`);
  else params.delete(K.sort);

  if (filter.drill) {
    put(K.drillSource, filter.drill.source);
    put(K.drillDimension, filter.drill.dimension);
    put(K.drillKey, filter.drill.key);
    put(K.drillLabel, filter.drill.label);
  } else {
    params.delete(K.drillSource);
    params.delete(K.drillDimension);
    params.delete(K.drillKey);
    params.delete(K.drillLabel);
  }

  if (filter.compareScope) {
    putIf(
      K.compareScopePortfolio,
      filter.compareScope.portfolioId,
      filter.compareScope.portfolioId == null
    );
    putIf(
      K.compareScopeBuilding,
      filter.compareScope.buildingId,
      filter.compareScope.buildingId == null
    );
  } else {
    params.delete(K.compareScopePortfolio);
    params.delete(K.compareScopeBuilding);
  }

  // Every legacy name is dropped on write, so a bookmark upgrades itself the
  // first time a control is touched instead of carrying both spellings forever.
  for (const legacy of Object.keys(LEGACY_KEYS)) params.delete(legacy);

  return params;
};

/* ────────────────────────────────  decode  ────────────────────────────── */

/**
 * `fr` / `to` ARE DAY KEYS NOW — and an old link still opens.
 *
 * A link shared before Plan C carries ISO instants there: `fr` at somebody's
 * local midnight and `to` at the midnight AFTER the last day (exclusive). It is
 * recognised by its `T` and read ONCE, on the device's clock: `fr` is that
 * instant's day; `to` is the day of the instant one millisecond earlier, which
 * is the last day that was picked. The hook then rewrites the address bar in
 * the new form (use-dashboard-filter.ts).
 *
 * Anything that is neither is dropped, and normalise() then falls back to a
 * named range rather than sending half a window.
 */
export const isLegacyInstant = (value: string | null): boolean =>
  Boolean(value) && /T/.test(value as string) && !Number.isNaN(Date.parse(value as string));

const dayParam = (value: string | null, end: 'from' | 'to'): string | null => {
  if (!value) return null;
  if (isDayKey(value)) return value;
  if (!isLegacyInstant(value)) return null;
  const at = Date.parse(value) - (end === 'to' ? 1 : 0);

  return dayKeyIn(at, deviceZone()) || null;
};

/** Does this address still carry a window in the old, instant form? */
export const hasLegacyWindow = (params: URLSearchParams): boolean =>
  isLegacyInstant(params.get(K.from)) || isLegacyInstant(params.get(K.to));

const readInt = (raw: string | null): number | null => {
  if (raw == null || raw.trim() === '') return null;
  // Digits only. `parseInt('12abc')` is 12, and that is how a typo becomes a
  // confident answer about building 12 — the same guard hub.js:167 applies.
  if (!/^-?\d+$/.test(raw.trim())) return null;
  const value = Number(raw.trim());

  return Number.isSafeInteger(value) ? value : null;
};

/**
 * Parameters -> filter. Every value is then put through `normalise`, so an enum
 * member `meta` does not admit falls back to the default rather than reaching
 * the server as a 400.
 *
 * Nothing here throws. A hand-edited URL is a thing that happens, and a screen
 * that white-screens on one is worse than a screen that ignores it.
 */
export const decode = (
  params: URLSearchParams,
  meta?: FilterMeta,
  /** What an ABSENT key falls back to. Defaults to DEFAULT_FILTER, which is what
   *  every existing caller gets. The dashboard passes the role-tailored defaults
   *  (QA-07) for the ONE case where the address bar is completely empty — see
   *  use-dashboard-filter.ts for why any parameter at all disables that. */
  defaults: DashboardFilter = DEFAULT_FILTER
): DashboardFilter => {
  const raw = (key: string): string | null => {
    const direct = params.get(key);
    if (direct != null) return direct;
    // Fall back to whichever legacy name maps to this key.
    for (const [legacy, mapped] of Object.entries(LEGACY_KEYS)) {
      if (mapped === key) {
        const value = params.get(legacy);
        if (value != null) return value;
      }
    }

    return null;
  };

  const str = (key: string, fallback: string | null): string | null => {
    const value = raw(key);

    return value == null || value === '' ? fallback : value;
  };
  const list = (key: string): string[] => {
    const value = raw(key);

    return value == null || value === '' ? [] : splitList(value);
  };

  const d = defaults;

  const sortRaw = raw(K.sort);
  let sort: SortState | null = null;
  if (sortRaw) {
    const [column, dir] = sortRaw.split(':');
    if (column) sort = { column, dir: dir === 'desc' ? 'desc' : 'asc' };
  }

  const drillKey = raw(K.drillKey);
  const drill: DrillSelection | null = drillKey
    ? {
        source: raw(K.drillSource) || '',
        dimension: raw(K.drillDimension) || '',
        key: drillKey,
        label: raw(K.drillLabel) || drillKey,
      }
    : null;

  const csPortfolio = readInt(raw(K.compareScopePortfolio));
  const csBuilding = readInt(raw(K.compareScopeBuilding));
  const compareScope: CompareScope | null =
    csPortfolio != null || csBuilding != null
      ? { portfolioId: csPortfolio, buildingId: csBuilding }
      : null;

  const columnsRaw = raw(K.columns);

  const candidate: DashboardFilter = {
    portfolioId: readInt(raw(K.portfolioId)),
    buildingId: readInt(raw(K.buildingId)),
    unit: str(K.unit, d.unit),

    range: str(K.range, d.range) as string,
    from: dayParam(str(K.from, d.from), 'from'),
    to: dayParam(str(K.to, d.to), 'to'),
    granularity: (str(K.granularity, d.granularity) || d.granularity) as Granularity,
    compare: (str(K.compare, d.compare) || d.compare) as string,
    timeFrom: str(K.timeFrom, d.timeFrom),
    timeTo: str(K.timeTo, d.timeTo),

    measure: (str(K.measure, d.measure) || d.measure) as string,
    book: (str(K.book, d.book) || d.book) as Book,
    subject: (str(K.subject, d.subject) || d.subject) as string,
    turnoverBucket: (str(K.turnoverBucket, d.turnoverBucket) ||
      d.turnoverBucket) as Granularity,

    kinds: list(K.kinds),
    severity: str(K.severity, d.severity),
    groups: list(K.groups),
    actors: list(K.actors),
    role: str(K.role, d.role),
    direction: str(K.direction, d.direction) as Direction | null,
    status: str(K.status, d.status) as Status | null,
    priority: str(K.priority, d.priority),
    buildingActivity: str(K.buildingActivity, d.buildingActivity),
    visitorType: str(K.visitorType, d.visitorType),
    visitorDetails: str(K.visitorDetails, d.visitorDetails),
    preAuthorized: str(K.preAuthorized, d.preAuthorized),
    onPass: str(K.onPass, d.onPass),
    approval: str(K.approval, d.approval),
    parkingValidation: str(K.parkingValidation, d.parkingValidation),
    numberOfGuests: readInt(raw(K.numberOfGuests)),
    carrier: str(K.carrier, d.carrier),
    parcelStatus: str(K.parcelStatus, d.parcelStatus),
    name: str(K.name, d.name),
    lastName: str(K.lastName, d.lastName),

    q: str(K.q, d.q) || '',

    tab: (str(K.tab, d.tab) || d.tab) as Tab,
    drill,
    qualityDrill: str(K.qualityDrill, d.qualityDrill),
    seriesOff: list(K.seriesOff),
    density: (str(K.density, d.density) || d.density) as Density,
    columns: columnsRaw ? splitList(columnsRaw) : null,
    sort,
    page: readInt(raw(K.page)) ?? 0,
    compareScope,
  };

  return normalise(candidate, meta);
};

/* ────────────────────────────  the write path  ────────────────────────── */

/**
 * ═══ COMPOSED AGAINST THE LIVE URL, NEVER AGAINST REACT'S SNAPSHOT ═══
 *
 * `useSearchParams` is memoised on `location.search` from the last COMMITTED
 * render, and App.tsx enables v7_startTransition, so a navigation writes
 * `history.pushState` BEFORE React is notified. For one commit the address bar
 * and the hook disagree.
 *
 * Seeding a write from the hook therefore makes every control a whole-query
 * overwrite based on a stale copy: set the measure (URL becomes ?ms=incidents),
 * then open a drill-down before that commit lands, and the drill handler rebuilds
 * the query from a snapshot with no `ms` in it — so the measure is silently gone
 * and nothing on screen says why. That defect was found and fixed once already
 * (dashboard-analytics.tsx:272-302); this is the same fix, in one place, for
 * every control on the screen.
 *
 * `window.location.search` is the value that is already true.
 */
export const nextSearch = (filter: DashboardFilter): string => {
  const live =
    typeof window === 'undefined'
      ? new URLSearchParams()
      : new URLSearchParams(window.location.search);

  return encode(filter, live).toString();
};

/** The parameters that are true right now, for reading back. */
export const liveParams = (): URLSearchParams =>
  new URLSearchParams(
    typeof window === 'undefined' ? '' : window.location.search
  );

/** Exported for the tests, and so nothing else has to guess a key. */
export const URL_KEYS = K;
export const URL_LEGACY_KEYS = LEGACY_KEYS;
export const encodeList = joinList;
export const decodeList = splitList;
